import { useMemo } from "react";
import { Link, useParams } from "react-router-dom";
import ArticleCard from "@/components/base/ArticleCard";
import RecommendCard from "@/components/base/RecommendCard";
import Breadcrumb from "@/components/base/Breadcrumb";
import EmptyState from "@/components/base/EmptyState";
import SectionHeading from "@/components/base/SectionHeading";
import FavoriteButton from "@/pages/detail/components/FavoriteButton";
import ShareBar from "@/pages/detail/components/ShareBar";
import { useScrollToTop } from "@/hooks/useScrollToTop";
import { useDocumentMeta } from "@/hooks/useDocumentMeta";
import { formatDate, formatViews, getArticleById, getRelatedArticles } from "@/lib/articles";
import { categoryMeta } from "@/mocks/articles";

export default function DetailPage() {
  useScrollToTop();
  const { id } = useParams<{ id: string }>();
  const article = id ? getArticleById(id) : undefined;

  useDocumentMeta(
    article ? `${article.title} - 蜜桃33` : "内容未找到 - 蜜桃33",
    article ? article.summary : undefined,
  );

  const related = useMemo(
    () => (article ? getRelatedArticles(article, 3) : []),
    [article],
  );

  if (!article) {
    return (
      <div className="mx-auto w-full max-w-[1200px] px-4 py-16 md:px-6">
        <EmptyState
          icon="ri-file-unknow-line"
          title="没有找到这篇内容"
          description="内容可能已被移动或删除，你可以返回首页浏览其他资讯。"
          actionLabel="返回首页"
          actionTo="/"
        />
      </div>
    );
  }

  const category = categoryMeta[article.category];
  const isRecommend = article.category === "recommendations";

  return (
    <div className="w-full pb-16 pt-6">
      <div className="mx-auto w-full max-w-[1200px] px-4 md:px-6">
        <Breadcrumb
          items={[
            { label: category.name, to: `/category/${category.slug}` },
            { label: article.title },
          ]}
        />

        <div className="mt-6 grid grid-cols-1 gap-10 lg:grid-cols-[1fr_320px]">
          <article className="min-w-0">
            <Link
              to={`/category/${category.slug}`}
              className="inline-flex items-center gap-1.5 rounded-full bg-primary-100 px-3 py-1 text-xs font-semibold text-primary-700 transition-colors hover:bg-primary-200"
            >
              <i className={category.icon} />
              {category.name}
            </Link>

            <h1 className="mt-4 font-heading text-2xl font-bold leading-snug text-foreground-950 text-balance md:text-[34px]">
              {article.title}
            </h1>

            <div className="mt-4 flex flex-wrap items-center gap-x-5 gap-y-2 text-sm text-foreground-500">
              <span className="flex items-center gap-1.5">
                <i className="ri-user-line" />
                {article.author}
              </span>
              <span className="flex items-center gap-1.5">
                <i className="ri-newspaper-line" />
                {article.source}
              </span>
              <span className="flex items-center gap-1.5">
                <i className="ri-calendar-line" />
                {formatDate(article.publishedAt)}
              </span>
              <span className="flex items-center gap-1.5">
                <i className="ri-eye-line" />
                {formatViews(article.views)} 阅读
              </span>
            </div>

            <div className="mt-5 flex flex-wrap items-center justify-between gap-3 border-y border-background-200 py-3">
              <div className="flex flex-wrap items-center gap-2">
                <FavoriteButton articleId={article.id} />
              </div>
              <ShareBar />
            </div>

            <div className="mt-6 overflow-hidden rounded-lg">
              <img
                src={article.cover}
                alt={article.title}
                title={`${article.title} - 蜜桃33`}
                className={`mx-auto w-full object-cover ${
                  isRecommend ? "max-w-md rounded-lg" : "max-h-[440px]"
                }`}
              />
            </div>

            <div className="mt-8 flex flex-col gap-5">
              {article.content.map((paragraph, index) => (
                <p
                  key={index}
                  className="text-[15px] leading-[1.9] text-foreground-800 md:text-base"
                >
                  {paragraph}
                </p>
              ))}
            </div>

            <div className="mt-8 flex flex-wrap items-center gap-2">
              <span className="text-sm text-foreground-500">相关标签：</span>
              {article.tags.map((tag) => (
                <Link
                  key={tag}
                  to={`/search?q=${encodeURIComponent(tag)}`}
                  className="rounded-full border border-background-200 bg-background-100 px-3 py-1 text-xs font-medium text-foreground-700 transition-colors hover:border-primary-300 hover:text-primary-700"
                >
                  #{tag}
                </Link>
              ))}
            </div>

            <div className="mt-8">
              <Link
                to={`/category/${category.slug}`}
                className="inline-flex items-center gap-2 whitespace-nowrap rounded-md border border-background-300 px-4 py-2.5 text-sm font-medium text-foreground-700 transition-colors hover:border-primary-300 hover:text-primary-700"
              >
                <i className="ri-arrow-left-line" />
                返回「{category.name}」
              </Link>
            </div>
          </article>

          <aside className="lg:sticky lg:top-24 lg:self-start">
            <div className="rounded-lg border border-background-200 bg-background-50 p-5">
              <h3 className="flex items-center gap-2 text-sm font-semibold text-foreground-950">
                <i className="ri-information-line text-primary-600" />
                内容信息
              </h3>
              <dl className="mt-4 flex flex-col gap-3 text-sm">
                <div className="flex items-center justify-between gap-3">
                  <dt className="text-foreground-500">所属分类</dt>
                  <dd>
                    <Link
                      to={`/category/${category.slug}`}
                      className="font-medium text-primary-700 hover:underline"
                    >
                      {category.name}
                    </Link>
                  </dd>
                </div>
                <div className="flex items-center justify-between gap-3">
                  <dt className="text-foreground-500">作者</dt>
                  <dd className="text-foreground-800">{article.author}</dd>
                </div>
                <div className="flex items-center justify-between gap-3">
                  <dt className="text-foreground-500">来源</dt>
                  <dd className="text-foreground-800">{article.source}</dd>
                </div>
                <div className="flex items-center justify-between gap-3">
                  <dt className="text-foreground-500">发布时间</dt>
                  <dd className="text-foreground-800">{formatDate(article.publishedAt)}</dd>
                </div>
                <div className="flex items-center justify-between gap-3">
                  <dt className="text-foreground-500">热度</dt>
                  <dd className="text-foreground-800">{formatViews(article.views)}</dd>
                </div>
              </dl>
            </div>

            <div className="mt-6 rounded-lg bg-accent-100/70 p-5">
              <h3 className="flex items-center gap-2 text-sm font-semibold text-accent-900">
                <i className="ri-lightbulb-line" />
                阅读提示
              </h3>
              <p className="mt-2 text-xs leading-relaxed text-accent-900/80">
                本站内容为演示示例，用于展示娱乐资讯平台的内容结构与阅读体验。收藏功能会保存在你的浏览器本地。
              </p>
            </div>
          </aside>
        </div>

        {related.length > 0 && (
          <section className="mt-14" aria-label="相关推荐">
            <SectionHeading
              eyebrow="RELATED"
              icon="ri-links-line"
              title="相关推荐"
              description="与当前内容相关的其他资讯与作品，继续阅读不中断。"
            />
            {isRecommend ? (
              <div className="grid grid-cols-2 gap-4 sm:grid-cols-3">
                {related.map((item) => (
                  <RecommendCard key={item.id} article={item} />
                ))}
              </div>
            ) : (
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
                {related.map((item) => (
                  <ArticleCard key={item.id} article={item} />
                ))}
              </div>
            )}
          </section>
        )}
      </div>
    </div>
  );
}